import { hasPrivilege } from 'cadenza/access-management/model/object-privilege-util';
import {
  type NavigatorFilterItemType,
  getNavigatorFilterItemType,
} from 'cadenza/navigator/navigator-tree/nav-tree-util';
import type { NavigatorTreeNodeDto } from 'cadenza/repository/model/navigator-tree-node-dto';

const SPECIAL_FILTER_TOKEN_PREFIX = 'SPECIAL_FILTER_TOKEN_';

export type UnprefixedSpecialFilterToken = NavigatorFilterItemType | 'SHARED' | 'WITH_LABEL';
export type SpecialFilterToken = `${typeof SPECIAL_FILTER_TOKEN_PREFIX}${UnprefixedSpecialFilterToken}`;

export function prefix(token: UnprefixedSpecialFilterToken): SpecialFilterToken {
  return `${SPECIAL_FILTER_TOKEN_PREFIX}${token}`;
}

export function unprefix(value: SpecialFilterToken) {
  return value.substring(SPECIAL_FILTER_TOKEN_PREFIX.length) as UnprefixedSpecialFilterToken;
}

export function isSpecialFilterToken(value: string): value is SpecialFilterToken {
  return value.startsWith(SPECIAL_FILTER_TOKEN_PREFIX);
}

export class NavigatorTreeFilter {
  _withLabel;
  _shared;
  _types;
  _terms;

  static of(tokens: string[]) {
    let shared = false;
    let withLabel = false;
    const types: NavigatorFilterItemType[] = [];
    const terms: string[] = [];

    for (const it of tokens) {
      if (isSpecialFilterToken(it)) {
        const token = unprefix(it);
        if (token === 'SHARED') {
          shared = true;
        } else if (token === 'WITH_LABEL') {
          withLabel = true;
        } else {
          types.push(token);
        }
      } else {
        terms.push(it);
      }
    }

    return new NavigatorTreeFilter(withLabel, shared, types, terms);
  }

  constructor(withLabel: boolean, shared: boolean, types: NavigatorFilterItemType[], terms: string[]) {
    this._withLabel = withLabel;
    this._shared = shared;
    this._types = types;
    this._terms = terms;
  }

  /**
   * Determines if the filter includes the given tree node or not.
   *
   * @param node - Tree node to check
   * @return An object containing a boolean flag indicating if the node should be included,
   * a list of the used search terms,
   * and if there was any, a map of matching labels (key) with an array of the search terms matching them (value).
   */
  includesNode(node: NavigatorTreeNodeDto) {
    const { allTermsMatch, matchingLabels } = this._allTermsMatch(node);

    const nodeIncluded =
      allTermsMatch && this._typesMatch(node) && this._withLabelMatches(node) && this._sharedMatches(node);

    return {
      nodeIncluded,
      matchingLabels,
    };
  }

  _typesMatch(node: NavigatorTreeNodeDto) {
    return this._types.length === 0 || this._types.includes(getNavigatorFilterItemType(node)!);
  }

  _withLabelMatches(node: NavigatorTreeNodeDto) {
    const labels = node.labels || [];
    return !this._withLabel || labels.length > 0;
  }

  _sharedMatches(node: NavigatorTreeNodeDto) {
    return !this._shared || hasPrivilege('privileges' in node ? node.privileges : null, 'share');
  }

  /**
   * Check if the filter's terms match with the given node, and return a boolean and an map object
   * containing all the matching labels (key) with the terms that matched them (value).
   * This map is used to render the matching labels with the highlighted search terms.
   *
   * @param node - The node to check
   * @return `true` if the node matches
   */
  _allTermsMatch(node: NavigatorTreeNodeDto) {
    const termsByLabels: Record<string, string[]> = {};

    for (const searchTerm of this._terms) {
      const { matches, matchingLabels } = this._termMatches(searchTerm, node);
      if (!matches) {
        return { allTermsMatch: false };
      }
      if (matchingLabels) {
        for (const matchingLabel of matchingLabels) {
          if (!termsByLabels[matchingLabel]) {
            termsByLabels[matchingLabel] = [];
          }
          termsByLabels[matchingLabel].push(searchTerm);
        }
      }
    }

    return {
      allTermsMatch: true,
      matchingLabels: termsByLabels,
    };
  }

  _termMatches(term: string, node: NavigatorTreeNodeDto) {
    term = term.toLowerCase();

    const textMatches = node.text.toLowerCase().includes(term);

    const labels = node.labels || [];

    const matchingLabels = labels.filter((label) => label.toLowerCase().includes(term));

    return {
      matches: textMatches || matchingLabels.length > 0,
      matchingLabels,
    };
  }

  get empty() {
    return !this._withLabel && !this._shared && this._types.length === 0 && this._terms.length === 0;
  }

  get terms() {
    return this._terms;
  }
}
